import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { currencyApi, Currency, ConversionResponse, CurrencyRate } from '../services/api/currency';
import { formatCurrency, formatCurrencyCompact } from '../utils/formatters';

interface CurrencyContextType {
    // Current settings
    userCurrency: string;
    availableCurrencies: Currency[];
    conversionRates: Record<string, Record<string, number>>; // from -> to -> rate

    // Loading states
    isLoadingRates: boolean;
    isLoadingSupportedCurrencies: boolean;

    // Core functions
    convertAmount: (amount: number, from: string, to?: string) => Promise<ConversionResponse>;
    formatCurrency: (amount: number, currency?: string, locale?: string) => string;
    formatCurrencyCompact: (amount: number, currency?: string, locale?: string) => string;
    setUserCurrency: (currency: string) => void;
    refreshRates: () => Promise<void>;

    // Advanced functions
    getRate: (from: string, to: string) => Promise<number>;
    getCachedRate: (from: string, to: string) => number | null;
    isRateAvailable: (from: string, to: string) => boolean;

    // Utility functions
    detectUserCurrency: () => Promise<void>;
    preloadPopularRates: () => Promise<void>;
    getCurrencyInfo: (code: string) => Currency | undefined;

    // Error state
    error: string | null;
    clearError: () => void;
}

interface CurrencyProviderProps {
    children: React.ReactNode;
    defaultCurrency?: string;
    autoDetectCurrency?: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Popular currency pairs for preloading
const POPULAR_CURRENCIES = ['USD', 'EUR', 'GBP', 'GHS', 'NGN', 'KES', 'ZAR'];
const USER_CURRENCY_KEY = 'sidego_user_currency';
const RATES_CACHE_KEY = 'sidego_currency_rates';
const RATES_CACHE_EXPIRY = 15 * 60 * 1000; // 15 minutes

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
    children,
    defaultCurrency = 'USD',
    autoDetectCurrency = true,
}) => {
    const [userCurrency, setUserCurrencyState] = useState<string>(
        localStorage.getItem(USER_CURRENCY_KEY) || defaultCurrency
    );
    const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>([]);
    const [conversionRates, setConversionRates] = useState<Record<string, Record<string, number>>>({});
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [isLoadingSupportedCurrencies, setIsLoadingSupportedCurrencies] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Use refs to prevent infinite re-renders
    const ratesCache = useRef<Map<string, { rate: number; timestamp: number }>>(new Map());
    const pendingRequests = useRef<Map<string, Promise<ConversionResponse>>>(new Map());

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // Set user currency with persistence
    const setUserCurrency = useCallback((currency: string) => {
        setUserCurrencyState(currency);
        localStorage.setItem(USER_CURRENCY_KEY, currency);
    }, []);

    // Get currency information
    const getCurrencyInfo = useCallback((code: string): Currency | undefined => {
        if (!Array.isArray(availableCurrencies)) {
            console.warn('availableCurrencies is not an array:', availableCurrencies);
            return undefined;
        }
        return availableCurrencies.find(currency => currency.code === code);
    }, [availableCurrencies]);

    // Load supported currencies
    const loadSupportedCurrencies = useCallback(async () => {
        if (isLoadingSupportedCurrencies) return;

        try {
            setIsLoadingSupportedCurrencies(true);
            setError(null);

            const currencies = await currencyApi.getSupportedCurrencies();

            // Validate that response is an array
            if (!Array.isArray(currencies)) {
                console.error('Invalid supported currencies response:', currencies);
                setError('Invalid currencies data received');
                setAvailableCurrencies([]);
                return;
            }

            setAvailableCurrencies(currencies);
        } catch (error) {
            console.error('Failed to load supported currencies:', error);
            setError('Failed to load supported currencies');
        } finally {
            setIsLoadingSupportedCurrencies(false);
        }
    }, []);

    // Get cached rate
    const getCachedRate = useCallback((from: string, to: string): number | null => {
        if (from === to) return 1;

        const cacheKey = `${from}_${to}`;
        const cached = ratesCache.current.get(cacheKey);

        if (cached && (Date.now() - cached.timestamp < RATES_CACHE_EXPIRY)) {
            return cached.rate;
        }

        // Check reverse rate
        const reverseCacheKey = `${to}_${from}`;
        const reverseCached = ratesCache.current.get(reverseCacheKey);

        if (reverseCached && (Date.now() - reverseCached.timestamp < RATES_CACHE_EXPIRY)) {
            return 1 / reverseCached.rate;
        }

        return null;
    }, []);

    // Check if rate is available in cache
    const isRateAvailable = useCallback((from: string, to: string): boolean => {
        return getCachedRate(from, to) !== null;
    }, [getCachedRate]);

    // Get rate with caching
    const getRate = useCallback(async (from: string, to: string): Promise<number> => {
        if (from === to) return 1;

        // Check cache first
        const cachedRate = getCachedRate(from, to);
        if (cachedRate !== null) {
            return cachedRate;
        }

        try {
            const rateData = await currencyApi.getRate(from, to);

            // Cache the rate
            const cacheKey = `${from}_${to}`;
            ratesCache.current.set(cacheKey, {
                rate: rateData.rate,
                timestamp: Date.now()
            });

            return rateData.rate;
        } catch (error) {
            console.error(`Failed to get rate from ${from} to ${to}:`, error);
            throw error;
        }
    }, [getCachedRate]);

    // Convert amount with caching and deduplication
    const convertAmount = useCallback(async (
        amount: number,
        from: string,
        to?: string
    ): Promise<ConversionResponse> => {
        const targetCurrency = to || userCurrency;

        if (from === targetCurrency) {
            return {
                originalAmount: amount,
                convertedAmount: amount,
                rate: 1,
                from,
                to: targetCurrency,
                timestamp: new Date(),
                cached: false
            };
        }

        // Check for pending request to avoid duplicates
        const requestKey = `${amount}_${from}_${targetCurrency}`;
        if (pendingRequests.current.has(requestKey)) {
            return pendingRequests.current.get(requestKey)!;
        }

        // Check cache first
        const cachedRate = getCachedRate(from, targetCurrency);
        if (cachedRate !== null) {
            return {
                originalAmount: amount,
                convertedAmount: amount * cachedRate,
                rate: cachedRate,
                from,
                to: targetCurrency,
                timestamp: new Date(),
                cached: true
            };
        }

        // Make request and cache it
        const promise = currencyApi.convertAmount({ amount, from, to: targetCurrency });
        pendingRequests.current.set(requestKey, promise);

        try {
            const result = await promise;

            // Cache the rate
            const cacheKey = `${from}_${targetCurrency}`;
            ratesCache.current.set(cacheKey, {
                rate: result.rate,
                timestamp: Date.now()
            });

            return result;
        } finally {
            pendingRequests.current.delete(requestKey);
        }
    }, [userCurrency, getCachedRate]);

    // Refresh rates
    const refreshRates = useCallback(async () => {
        if (isLoadingRates) return;

        try {
            setIsLoadingRates(true);
            setError(null);

            // Clear cache
            ratesCache.current.clear();
            setConversionRates({});

            // Load popular rates
            await preloadPopularRates();
        } catch (error) {
            console.error('Failed to refresh rates:', error);
            setError('Failed to refresh exchange rates');
        } finally {
            setIsLoadingRates(false);
        }
    }, []);

    // Preload popular currency rates
    const preloadPopularRates = useCallback(async () => {
        if (isLoadingRates) return;

        try {
            const baseCurrency = 'USD'; // Use USD as base for consistency
            const rates = await currencyApi.getRates(baseCurrency);

            // Update conversion rates state
            const newRates: Record<string, Record<string, number>> = {};
            newRates[baseCurrency] = rates;

            // Cache popular rates
            const timestamp = Date.now();
            Object.entries(rates).forEach(([currency, rate]) => {
                const cacheKey = `${baseCurrency}_${currency}`;
                ratesCache.current.set(cacheKey, { rate, timestamp });
            });

            setConversionRates(newRates);
        } catch (error) {
            console.error('Failed to preload rates:', error);
        }
    }, []);

    // Detect user currency based on location/preferences
    const detectUserCurrency = useCallback(async () => {
        try {
            // Try to detect from browser/locale
            const locale = navigator.language || 'en-US';
            let detectedCurrency = defaultCurrency;

            // Simple locale-to-currency mapping
            const localeMap: Record<string, string> = {
                'en-US': 'USD',
                'en-GB': 'GBP',
                'de': 'EUR',
                'fr': 'EUR',
                'es': 'EUR',
                'it': 'EUR',
                'zh': 'CNY',
                'ja': 'JPY',
                'ko': 'KRW',
                // Add more mappings as needed
            };

            const baseLocale = locale.split('-')[0];
            if (localeMap[locale]) {
                detectedCurrency = localeMap[locale];
            } else if (localeMap[baseLocale]) {
                detectedCurrency = localeMap[baseLocale];
            }

            // Only update if currency is supported
            if (availableCurrencies.some(c => c.code === detectedCurrency)) {
                setUserCurrency(detectedCurrency);
            }
        } catch (error) {
            console.warn('Currency detection failed:', error);
        }
    }, [availableCurrencies, defaultCurrency, setUserCurrency]);

    // Enhanced currency formatting functions
    const formatCurrencyValue = useCallback((
        amount: number,
        currency?: string,
        locale?: string
    ): string => {
        return formatCurrency(amount, currency || userCurrency, locale);
    }, [userCurrency]);

    const formatCurrencyCompactValue = useCallback((
        amount: number,
        currency?: string,
        locale?: string
    ): string => {
        return formatCurrencyCompact(amount, currency || userCurrency, locale);
    }, [userCurrency]);

    // Initialize context
    useEffect(() => {
        loadSupportedCurrencies();
    }, [loadSupportedCurrencies]);

    // Auto-detect currency when currencies are loaded
    useEffect(() => {
        if (autoDetectCurrency && availableCurrencies.length > 0 && userCurrency === defaultCurrency) {
            detectUserCurrency();
        }
    }, [autoDetectCurrency, availableCurrencies.length, userCurrency, defaultCurrency, detectUserCurrency]);

    // Preload rates when user currency changes
    useEffect(() => {
        if (availableCurrencies.length > 0) {
            preloadPopularRates();
        }
    }, [userCurrency, availableCurrencies.length, preloadPopularRates]);

    // Clear cached rates when user currency changes
    useEffect(() => {
        // Clear rates involving the currency change
        const keysToDelete: string[] = [];
        ratesCache.current.forEach((_, key) => {
            if (key.includes(`_${userCurrency}`) || key.startsWith(`${userCurrency}_`)) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => ratesCache.current.delete(key));
    }, [userCurrency]);

    const contextValue: CurrencyContextType = {
        userCurrency,
        availableCurrencies,
        conversionRates,
        isLoadingRates,
        isLoadingSupportedCurrencies,
        convertAmount,
        formatCurrency: formatCurrencyValue,
        formatCurrencyCompact: formatCurrencyCompactValue,
        setUserCurrency,
        refreshRates,
        getRate,
        getCachedRate,
        isRateAvailable,
        detectUserCurrency,
        preloadPopularRates,
        getCurrencyInfo,
        error,
        clearError,
    };

    return (
        <CurrencyContext.Provider value={contextValue}>
            {children}
        </CurrencyContext.Provider>
    );
};

// Hook to use currency context
export const useCurrency = (): CurrencyContextType => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

export default CurrencyContext;